/* eslint-disable react/no-array-index-key */
// React
import React, { useMemo } from 'react';

// Components
import { TablePagination } from 'components/third-party/ReactTable';

// Packages
import { DefaultColumnFilter, renderFilterTypes } from 'utils/react-table';
import { useTable, useFilters, usePagination } from 'react-table';
import {
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Link,
    Skeleton,
    Tooltip,
    Typography
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { formatSeats, maxManagersOf, SEATS_TOOLTIP } from 'utils/helpers/manager-limit.helper';

const ReportsTable = ({ data }) => {
    const filterTypes = useMemo(() => renderFilterTypes, []);
    const defaultColumn = useMemo(() => ({ Filter: DefaultColumnFilter }), []);
    const initialState = useMemo(() => ({ filters: [], pageIndex: 0, pageSize: 10 }), []);

    const renderCountCell = ({ value }) => {
        if (value === undefined) return <Skeleton variant="text" width={40} />;
        if (value === null) return '—';
        return value;
    };

    const columns = useMemo(
        () => [
            {
                Header: 'Org ID',
                accessor: 'org_id',
                filter: 'fuzzyText'
            },
            {
                Header: 'Org Name',
                accessor: 'org_name',
                filter: 'fuzzyText'
            },
            {
                Header: 'Active Property Count',
                accessor: 'propertyCount',
                disableFilters: true,
                Cell: renderCountCell
            },
            {
                Header: 'LPR Plates',
                accessor: 'lprPlateCount',
                disableFilters: true,
                Cell: ({ value, row }) => {
                    if (value === undefined) return <Skeleton variant="text" width={40} />;
                    if (value === null) return '—';

                    const countsByRole = row.original.lprPlateCountByRole;
                    if (!countsByRole) return value;

                    return (
                        <Tooltip
                            arrow
                            placement="top"
                            title={
                                <Stack spacing={0.5}>
                                    {[
                                        ['resident', 'Residents'],
                                        ['tenant', 'Tenants'],
                                        ['operator', 'Operators'],
                                        ['nonMember', 'Non-members'],
                                        ['associate', 'Associates'],
                                        ['guest', 'Guests (this month)']
                                    ].map(([role, label]) => (
                                        <Stack key={role} direction="row" justifyContent="space-between" spacing={2}>
                                            <Typography variant="caption">{label}</Typography>
                                            <Typography variant="caption">{countsByRole[role] ?? '—'}</Typography>
                                        </Stack>
                                    ))}
                                </Stack>
                            }
                        >
                            <Typography component="span" variant="inherit" sx={{ cursor: 'help', borderBottom: '1px dotted' }}>
                                {value}
                            </Typography>
                        </Tooltip>
                    );
                }
            },
            {
                Header: 'Mobile Credentials',
                accessor: 'mobileCredentialCount',
                disableFilters: true,
                Cell: renderCountCell
            },
            {
                // Managers in the org out of its limit; invited managers are not counted here.
                Header: 'Seats',
                id: 'seats',
                accessor: row => formatSeats(row.users, maxManagersOf(row)),
                disableFilters: true,
                Cell: ({ value }) => (
                    <Tooltip arrow placement="top" title={SEATS_TOOLTIP}>
                        <Typography
                            component="span"
                            variant="inherit"
                            sx={{ cursor: 'help', borderBottom: '1px dotted' }}
                        >
                            {value}
                        </Typography>
                    </Tooltip>
                )
            }
        ],
        []
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        page,
        prepareRow,
        gotoPage,
        setPageSize,
        state: { pageIndex, pageSize }
    } = useTable(
        {
            columns,
            data,
            defaultColumn,
            initialState,
            filterTypes,
            autoResetPage: false,
            autoResetFilters: false,
            autoResetSortBy: false
        },
        useFilters,
        usePagination
    );
    return (
        <Stack>
            <Table {...getTableProps()}>
                <TableHead sx={{ borderTopWidth: 1 }}>
                    {headerGroups.map((headerGroup, index) => (
                        <TableRow {...headerGroup.getHeaderGroupProps()} key={index}>
                            {headerGroup.headers.map((column, i) => (
                                <TableCell
                                    {...column.getHeaderProps([
                                        { className: column.className }
                                    ])}
                                    key={i}
                                >
                                    {column.render('Header')}
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableHead>
                <TableBody {...getTableBodyProps()}>
                    {headerGroups.map((group, i) => (
                        <TableRow {...group.getHeaderGroupProps()} key={i}>
                            {group.headers.map((column, index) => (
                                <TableCell
                                    {...column.getHeaderProps([
                                        { className: column.className }
                                    ])}
                                    key={index}
                                >
                                    {column.canFilter ? column.render('Filter') : null}
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                    {page.map((row, i) => {
                        prepareRow(row);
                        return (
                            <TableRow
                                hover
                                role="checkbox"
                                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                tabIndex={-1}
                                {...row.getRowProps()}
                                key={i}
                            >
                                {row.cells.map((cell, index) => (
                                    <TableCell
                                        {...cell.getCellProps([
                                            { className: cell.column.className }
                                        ])}
                                        align={'left'}
                                        key={index}
                                    >
                                        {cell.column.id === 'org_id' ? (
                                            <Link
                                                color="secondary"
                                                component={RouterLink}
                                                to={`/orgs/${cell.value}`}
                                            >
                                                {cell.render('Cell')}
                                            </Link>
                                        ) : (
                                            cell.render('Cell')
                                        )}
                                    </TableCell>
                                ))}
                            </TableRow>
                        );
                    })}

                    <TableRow>
                        <TableCell sx={{ p: 2 }} colSpan={6}>
                            <TablePagination
                                gotoPage={gotoPage}
                                rows={rows}
                                setPageSize={setPageSize}
                                pageIndex={pageIndex}
                                pageSize={pageSize}
                            />
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </Stack>
    );
};

export default ReportsTable;
